import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Download, Plus, Users, FileText, BarChart3, 
  Trash2, User, Calendar, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterviewResponsesPage = () => {
  const { projectId, interviewId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedInterviewee, setExpandedInterviewee] = useState(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/interviews/${interviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterview(res.data);
    } catch (err) {
      toast.error('Failed to load interview data');
      navigate(`/project/${projectId}/data-collection`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [interviewId, projectId, token, navigate]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/interviews/${interviewId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `interview_${interviewId.slice(0, 8)}_responses.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const handleDeleteResponse = async (intervieweeId) => {
    if (!window.confirm('Delete this interview response?')) return;
    try {
      await axios.delete(`${API}/interviews/${interviewId}/responses/${intervieweeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Response deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete response');
    }
  };

  const handleStartAnalysis = () => {
    navigate(`/project/${projectId}?from=interview&interviewId=${interviewId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const interviewees = interview?.interviewees || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}/data-collection`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">{interview?.title}</h1>
                <p className="text-sm text-slate-500">Interview Responses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to={`/project/${projectId}/interview/${interviewId}/record`}>
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Response
                </Button>
              </Link>
              {interviewees.length > 0 && (
                <>
                  <Button variant="outline" onClick={handleExport}>
                    <Download className="w-4 h-4 mr-2" />
                    Export TXT
                  </Button>
                  <Button onClick={handleStartAnalysis} className="bg-emerald-600 hover:bg-emerald-700">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Start Analysis
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{interviewees.length}</p>
                <p className="text-sm text-slate-500">
                  {interview?.target_interviewees ? `of ${interview.target_interviewees}` : ''} Interviews
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{interview?.questions?.length || 0}</p>
                <p className="text-sm text-slate-500">Questions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {interviewees.reduce((sum, i) => sum + Object.values(i.answers || {}).filter(a => a).length, 0)}
                </p>
                <p className="text-sm text-slate-500">Total Responses</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {interview?.target_interviewees && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Collection Progress</span>
              <span className="text-sm text-slate-500">
                {Math.round((interviewees.length / interview.target_interviewees) * 100)}%
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-emerald-600 h-3 rounded-full transition-all"
                style={{ width: `${Math.min((interviewees.length / interview.target_interviewees) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Interviewees List */}
        {interviewees.length > 0 ? (
          <div className="space-y-4">
            {interviewees.map((interviewee, idx) => (
              <div 
                key={interviewee.interviewee_id} 
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedInterviewee(
                    expandedInterviewee === interviewee.interviewee_id ? null : interviewee.interviewee_id
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {interviewee.interviewee_name || `Participant ${idx + 1}`}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(interviewee.recorded_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {Object.values(interviewee.answers || {}).filter(a => a).length} responses
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteResponse(interviewee.interviewee_id);
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedInterviewee === interviewee.interviewee_id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                
                {/* Expanded Content */}
                {expandedInterviewee === interviewee.interviewee_id && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    <div className="space-y-4">
                      {interview?.questions?.map((question, qIdx) => (
                        <div key={question.id} className="bg-white rounded-lg p-4">
                          <p className="text-sm font-medium text-emerald-600 mb-1">Q{qIdx + 1}</p>
                          <p className="font-medium text-slate-900 mb-2">{question.question}</p>
                          <p className="text-slate-700 whitespace-pre-wrap">
                            {interviewee.answers?.[question.id] || (
                              <span className="text-slate-400 italic">No response recorded</span>
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No interviews recorded yet</h3>
            <p className="text-slate-500 mb-4">Start recording interview responses to collect qualitative data</p>
            <Link to={`/project/${projectId}/interview/${interviewId}/record`}>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Record First Interview
              </Button>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default InterviewResponsesPage;
